import React from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import { jobPhaseCodesAtom } from '../../store/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { sectionByKey } from './sectionDefs';
import SectionTable from './SectionTable';
import WorkPhaseChips from './WorkPhaseChips';
import LaborMatrix from './LaborMatrix';
import EquipmentMatrix from './EquipmentMatrix';
import SubDocsUpload from './SubDocsUpload';
import MaterialDeliveryCards from './MaterialDeliveryCards';
import QualityChecklist from './QualityChecklist';
import IssueCards from './IssueCards';

// Generic rows editor: every section step is this component with a different
// section key — the config in sectionDefs drives the fields and totals.
const SectionRowsStep = ({ sectionKey, readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const def = sectionByKey[sectionKey];
    const rows = report?.sections?.[sectionKey] || [];

    const setRows = (next) => {
        setReport({ ...report, sections: { ...report.sections, [sectionKey]: next } });
        setDirty(true);
    };

    const addRow = () => setRows([...rows, {}]);
    const updateRow = (i) => (field, value) => {
        const next = rows.slice();
        next[i] = { ...next[i], [field]: value };
        const fdef = def.fields.find((f) => f.name === field);
        if (def.autoAddOnCostCode && fdef?.type === 'costCode' && i === next.length - 1
            && phaseCodes?.[String(value).trim()]) next.push({});
        setRows(next);
    };
    const deleteRow = (i) => () => setRows(rows.filter((_, idx) => idx !== i));

    const totals = def.totals ? def.totals(rows) : null;

    const totalsFooter = (tt, rr) => tt && rr.length > 0 && (
        <View style={{ borderRadius: 10, backgroundColor: '#F1F5F9', padding: 12, flexDirection: 'row', flexWrap: 'wrap', columnGap: 18, rowGap: 8, marginTop: 12 }}>
            {tt.map((t) => (
                <View key={t.label}>
                    <Text style={{ fontSize: 10, fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</Text>
                    <Text style={{ fontSize: 16, fontWeight: '700', color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>{t.value}</Text>
                </View>
            ))}
        </View>
    );

    // Labor & Equipment share one step: both are phases-as-columns matrices.
    if (def.matrix) {
        const equipDef = sectionByKey.equipment;
        const equipRows = report?.sections?.equipment || [];
        const equipTotals = equipDef.totals ? equipDef.totals(equipRows) : null;
        return (
            <View style={{ paddingBottom: 8 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginBottom: 8 }}>Labor & Crew On Site</Text>
                <LaborMatrix readOnly={readOnly} />
                {/* Sub time tickets ride inside the headcount totals bar. */}
                <SubDocsUpload readOnly={readOnly} totals={rows.length > 0 ? totals : []} />
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginTop: 20, marginBottom: 8 }}>Equipment Used</Text>
                <EquipmentMatrix readOnly={readOnly} />
                {totalsFooter(equipTotals, equipRows)}
            </View>
        );
    }

    if (def.deliveryCards) {
        return (
            <View style={{ paddingBottom: 8 }}>
                <MaterialDeliveryCards readOnly={readOnly} />
            </View>
        );
    }

    // Issue Log renders as condensed cards (no sideways scroll on phones).
    if (sectionKey === 'delays') {
        return (
            <View style={{ paddingBottom: 8 }}>
                <IssueCards readOnly={readOnly} />
            </View>
        );
    }

    // The Quality step is just the activity checklist — no item rows.
    if (sectionKey === 'inspections') {
        return (
            <View style={{ paddingBottom: 8 }}>
                <QualityChecklist readOnly={readOnly} />
            </View>
        );
    }

    return (
        <View style={{ paddingBottom: 8 }}>
            {sectionKey === 'work' && <WorkPhaseChips readOnly={readOnly} />}
            {rows.length === 0 && (
                <View style={{ borderRadius: 12, border: '1px dashed #CBD5E1', padding: 24, alignItems: 'center', marginBottom: 12, backgroundColor: '#F8FAFC' }}>
                    <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center' }}>
                        {readOnly ? `No ${def.rowNoun} entries on this report.` : `Nothing here yet — add the first ${def.rowNoun} below, or tap Next if none today.`}
                    </Text>
                </View>
            )}
            {rows.length > 0 && (
                <SectionTable
                    def={def}
                    rows={rows}
                    readOnly={readOnly}
                    phaseCodes={phaseCodes}
                    onChange={updateRow}
                    onDelete={deleteRow}
                />
            )}
            {!readOnly && (
                <Pressable
                    onPress={addRow}
                    style={{ cursor: 'pointer', paddingTop: 4, paddingBottom: 4, marginBottom: 12, alignSelf: 'flex-start' }}
                >
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>{`+ Add ${def.rowNoun}`}</Text>
                </Pressable>
            )}
            {totals && rows.length > 0 && (
                <View style={{ borderRadius: 10, backgroundColor: '#F1F5F9', padding: 12, flexDirection: 'row', flexWrap: 'wrap', columnGap: 18, rowGap: 8 }}>
                    {totals.map((t) => (
                        <View key={t.label}>
                            <Text style={{ fontSize: 10, fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</Text>
                            <Text style={{ fontSize: 16, fontWeight: '700', color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>{t.value}</Text>
                        </View>
                    ))}
                </View>
            )}
        </View>
    );
};

export default SectionRowsStep;
